/**
 * Date formatting helpers (Day 12).
 *
 * Standardise on "short month + numeric day + year" everywhere ("Apr 26, 2026")
 * for consistency with the time-off / audit views.
 *
 * Important: `new Date('YYYY-MM-DD')` parses as UTC and shifts in negative-
 * offset zones. For ISO date strings (no time component) we always parse
 * the components manually to keep the calendar day stable.
 */

/** Returns "Apr 26, 2026" for either an ISO date ("2026-04-26") or a Date. */
export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d =
    typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)
      ? (() => {
          const [y, m, day] = input.split('-').map(Number);
          return new Date(y, m - 1, day);
        })()
      : new Date(input);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Returns "Sunday, Apr 26, 2026" for an ISO date string or Date. */
export function formatDateLong(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d =
    typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)
      ? (() => {
          const [y, m, day] = input.split('-').map(Number);
          return new Date(y, m - 1, day);
        })()
      : new Date(input);
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Returns "Apr 26, 2026, 2:30 PM" for a timestamptz string. */
export function formatDateTime(input: string | Date | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
